/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import {
  LitElement,
  PropertyValueMap,
  TemplateResult,
  css,
  html,
  nothing,
} from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

import {
  ConfigurationPrintOptionDto2,
  PersonalizationTypeEnum,
  PrintOptionDto,
  SelectedPrintOptionDto,
} from '@/api-sdk';
import { FgDialog } from '@/components';
import '@/components/fg-dialog';
import { iconChevronLeft } from '@/components/icons';
import {
  GlobalComponentType,
  MobileContext,
  PersonalisationType as PersonalisationContextType,
  UploadServiceContext,
  global,
  personalisation,
  uploadService,
} from '@/context';
import {
  FLOW_LOGO,
  FLOW_LOGO_FULL_COLOR,
  FLOW_LOGO_TEXT,
  FLOW_LOGO_TEXT_FULL_COLOR,
  FLOW_TEXT,
  FLOW_TEXT_FULL_COLOR,
  STEP_DONE,
} from '@/data';
import { baseStyles, buttonStyles } from '@/styles';
import {
  ChosenPersonalisationType,
  GTMEventType,
  PersonalisationFlow,
  PersonalisationStep,
  PersonalisationStepId,
  PersonalisationType,
} from '@/types';
import { sendGTMEvent } from '@/utils';

import '@/components/accessibility/fg-aria-live';
import './fg-visual-designer';
import './step/fg-personalisation-choose-colors-step';
import './step/fg-personalisation-choose-type';
import './step/fg-personalisation-choose-visual-designer';
import './step/fg-personalisation-color-option-step';
import './step/fg-personalisation-error';
import './step/fg-personalisation-file';
import './step/fg-personalisation-step-done';
import './step/fg-personalisation-text-step';
import './step/fg-personalisation-uploading-step';
import './step/fg-upload-file';

/**
 * @element fg-personalisation
 */
@customElement('fg-personalisation')
@localized()
export class FgPersonalisation extends LitElement {
  private fgDialogRef: Ref<FgDialog> = createRef();

  @property({ type: Object })
  printOption?:
    | PrintOptionDto
    | ConfigurationPrintOptionDto2
    | SelectedPrintOptionDto;

  @property({ type: Boolean })
  hasColorChoice? = true;

  @property({ type: Boolean })
  isVisualDesignerAvailable = false;

  @state()
  private stepIndex = 0;

  @state()
  private flow: PersonalisationFlow = FLOW_LOGO_TEXT;

  @state()
  private fileName?: string;

  @state()
  private file?: File;

  @state()
  private stepSkipped = false;

  @state()
  private errorMessage?: string;

  @state()
  private chosenPersonalisation = ChosenPersonalisationType.DEFAULT;

  @state()
  announcement: string = '';

  @state()
  designId: string = '';

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: uploadService, subscribe: true })
  uploadService: UploadServiceContext = undefined;

  @consume({ context: personalisation.context, subscribe: true })
  personalisationContext!: PersonalisationContextType[];

  @consume({ context: MobileContext, subscribe: true })
  isMobile: boolean = false;

  public openDialog() {
    const visualDesignId = this.personalisationContext[0]?.visualDesignId;
    if (visualDesignId) this.designId = visualDesignId;

    this.errorMessage = undefined;
    this.stepIndex = 0;
    this.fgDialogRef.value?.open();
    this.chosenPersonalisation = ChosenPersonalisationType.DEFAULT;
  }

  public closeDialog() {
    this.fgDialogRef.value?.close();
    this.chosenPersonalisation = ChosenPersonalisationType.MANUAL;
  }

  protected willUpdate(_changedProperties: PropertyValueMap<any>) {
    if (
      this.printOption &&
      this.getCurrentStep().id === PersonalisationStepId.DONE &&
      _changedProperties.has('stepIndex')
    ) {
      sendGTMEvent(GTMEventType.PRODUCT_CONFIGURATOR, {
        configurator_step_name: 'print_delivery',
        configurator_step: '3',
        configurator_sub_step: '4',
        configuration_sub_step_name: 'print_selection_success',
        interaction_input: this.printOption.code,
      });
    }
  }

  private setAnnouncement(event: CustomEvent) {
    this.announcement = event.detail.announcement;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(
      'handle-announcement',
      this.setAnnouncement as EventListener,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(
      'handle-announcement',
      this.setAnnouncement as EventListener,
    );
  }

  private onSelectFlow(event: CustomEvent) {
    const { type } = event.detail;

    switch (type) {
      case PersonalisationType.LOGO:
        if (this.hasColorChoice) {
          this.flow = FLOW_LOGO;
          break;
        }
        this.flow = FLOW_LOGO_FULL_COLOR;
        break;
      case PersonalisationType.LOGO_TEXT:
        if (this.hasColorChoice) {
          this.flow = FLOW_LOGO_TEXT;
          break;
        }
        this.flow = FLOW_LOGO_TEXT_FULL_COLOR;
        break;
      case PersonalisationType.TEXT:
        if (this.hasColorChoice) {
          this.flow = FLOW_TEXT;
          break;
        }
        this.flow = FLOW_TEXT_FULL_COLOR;
        break;
    }

    if (this.printOption) {
      personalisation.providerUpdateEvent(this, {
        printOptionCode: this.printOption.code,
        type,
        colors: [],
        textPersonalization: null,
        logoUuid: null,
      });
    }

    this.stepIndex = 1;
  }

  private goBack() {
    this.errorMessage = undefined;
    //Prevent uploading to be fired again when going back from color source step
    if (this.getCurrentStep().id === PersonalisationStepId.COLORS_SOURCE) {
      this.stepIndex = PersonalisationStepId.UPLOAD_SELECT_FILE;
    }
    if (this.stepIndex > 0 || !this.stepSkipped) {
      this.stepIndex--;
    }
    // Check if a step is skipped and go back 2 steps
    if (this.stepSkipped) {
      this.stepIndex = this.stepIndex - 2;
      // Reset the step skipped state after going back
      this.stepSkipped = false;
    }
  }

  private goFurther() {
    this.errorMessage = undefined;
    if (this.flow !== undefined && this.stepIndex < this.flow.length - 1) {
      this.stepIndex++;
    }
  }

  private getCurrentStep(): PersonalisationStep {
    return this.flow[this.stepIndex];
  }

  private getStepTitle(): string {
    return t(this.getCurrentStep().titleTranslationKey);
  }

  private getAriaLabelBackButton(): string {
    if (this.stepIndex <= 0) {
      return '';
    }

    const previousStep = this.flow[this.stepIndex - 1];
    const previousStepTitle = t(previousStep.titleTranslationKey);

    return t('actions.backTo', { stepName: previousStepTitle });
  }

  private getBackButton(): TemplateResult {
    if (
      this.stepIndex === 0 ||
      this.getCurrentStep().id === PersonalisationStepId.DONE
    ) {
      return html``;
    }

    return html`
      <button
        class="previous-button"
        @click=${() => this.goBack()}
        aria-label="${this.getAriaLabelBackButton()}"
      >
        <div class="previous-icon">${iconChevronLeft}</div>
        ${t('actions.back')}
      </button>
    `;
  }

  private goToUpload(event: CustomEvent) {
    const { name: uploadedFileName } = event.detail;

    if (this.printOption) {
      personalisation.providerUpdateEvent(
        this,
        { uploadedFileName },
        this.printOption.code,
      );
    }

    this.fileName = uploadedFileName;
    this.file = event.detail;

    this.goFurther();
  }

  private uploadDone(event: CustomEvent) {
    if (this.errorMessage && this.errorMessage.length > 0) {
      this.errorMessage = undefined;
    }

    if (this.printOption) {
      personalisation.providerUpdateEvent(
        this,
        { logoUuid: event.detail },
        this.printOption.code,
      );
    }

    this.dispatchEvent(
      new CustomEvent('handle-announcement', {
        composed: true,
        detail: {
          announcement: t('labels.uploadFileSuccess'),
        },
      }),
    );

    this.goFurther();
  }

  private goToStep(event: CustomEvent<PersonalisationStepId>) {
    if (!event.detail) {
      this.goFurther();
      return;
    }
    // Step index is incremented by 2 to skip the colors selection step
    this.stepIndex = this.stepIndex + 2;
    this.stepSkipped = true;
  }

  private getTextContent(event: CustomEvent) {
    const { textPersonalization } = event.detail;

    if (this.printOption) {
      personalisation.providerUpdateEvent(
        this,
        { textPersonalization },
        this.printOption.code,
      );
    }

    this.goFurther();
  }

  private resetUpload(event: CustomEvent) {
    this.fileName = undefined;
    this.stepIndex = event.detail;
    return;
  }

  private uploadError(event: CustomEvent) {
    if (event.detail.error && typeof event.detail.error === 'string') {
      this.errorMessage = event.detail.error;

      this.dispatchEvent(
        new CustomEvent('handle-announcement', {
          composed: true,
          detail: {
            announcement: event.detail.error,
          },
        }),
      );
    }
    this.stepIndex = PersonalisationStepId.UPLOAD_SELECT_FILE;
  }

  private pmsColors(event: CustomEvent) {
    const { colors } = event.detail;

    if (this.printOption) {
      personalisation.providerUpdateEvent(
        this,
        { colors },
        this.printOption.code,
      );
    }

    this.goFurther();
  }

  private removePersonalisationData() {
    this.uploadService?.controller?.abort();
    if (
      !this.printOption ||
      this.flow[this.stepIndex] === STEP_DONE ||
      this.chosenPersonalisation ===
        ChosenPersonalisationType.VISUAL_DESIGNER ||
      personalisation.getChosenVariant(this.personalisationContext) ===
        'visual_design'
    ) {
      this.chosenPersonalisation = ChosenPersonalisationType.DEFAULT;
      return;
    }
    personalisation.providerRemoveEvent(this, this.printOption.code);
  }

  private getStepContent(): TemplateResult {
    switch (this.getCurrentStep().id) {
      default:
        return html`
          <fg-personalisation-choose-type
            @personalisation-choose-type=${this.onSelectFlow}
          ></fg-personalisation-choose-type>
        `;
      case PersonalisationStepId.UPLOAD_SELECT_FILE:
        return html`
          <fg-upload-file @upload-file=${this.goToUpload}></fg-upload-file>
        `;
      case PersonalisationStepId.UPLOAD_PROCESS:
        return html`
          <fg-personalisation-uploading
            .file=${this.file}
            @personalisation-uploaded=${this.uploadDone}
            @upload-error=${this.uploadError}
          ></fg-personalisation-uploading>
        `;
      case PersonalisationStepId.COLORS_SOURCE:
        return html`
          <fg-personalisation-color-option-step
            .fileName=${this.fileName}
            .selectedFlow=${this.flow}
            @personalisation-proceed-step=${this.goToStep}
            @personalisation-reset-upload-file=${this.resetUpload}
          ></fg-personalisation-color-option-step>
        `;
      case PersonalisationStepId.PICK_COLORS:
        return html`
          <fg-personalisation-choose-colors-step
            .colorAmount=${this.printOption?.colorCount}
            @personalisation-colors=${this.pmsColors}
          ></fg-personalisation-choose-colors-step>
        `;
      case PersonalisationStepId.PICK_COLORS_WITH_FILE:
        return html`
          <fg-personalisation-choose-colors-step
            .fileName=${this.fileName}
            .colorAmount=${this.printOption?.colorCount}
            @personalisation-colors=${this.pmsColors}
            @personalisation-reset-upload-file=${this.resetUpload}
          ></fg-personalisation-choose-colors-step>
        `;
      case PersonalisationStepId.TEXT:
        return html`
          <fg-personalisation-text-step
            @personalisation-text-step=${this.getTextContent}
            .selectedFlow=${this.flow}
          ></fg-personalisation-text-step>
        `;
      case PersonalisationStepId.TEXT_WITH_FILE:
        return html`
          <fg-personalisation-text-step
            @personalisation-text-step=${this.getTextContent}
            .selectedFlow=${this.flow}
            .fileName=${this.fileName}
            @personalisation-reset-upload-file=${this.resetUpload}
          ></fg-personalisation-text-step>
        `;
      case PersonalisationStepId.DONE:
        return html`
          <fg-personalisation-step-done
            .fileName=${this.fileName}
            @personalisation-completed=${this.closeDialog}
          ></fg-personalisation-step-done>
        `;
    }
  }

  private visualDesignerSave(event: CustomEvent) {
    const { visualDesignId } = event.detail;

    if (!visualDesignId) {
      return;
    }

    if (this.printOption) {
      personalisation.providerUpdateEvent(this, {
        isCompleted: true,
        printOptionCode: this.printOption.code,
        type: PersonalizationTypeEnum.visual_design,
        colors: [],
        visualDesignId: visualDesignId,
      });
    }

    this.designId = visualDesignId;

    this.closeDialog();
  }

  render() {
    return html`
      <fg-dialog
        ${ref(this.fgDialogRef)}
        .fullscreen=${Boolean(
          this.chosenPersonalisation ===
            ChosenPersonalisationType.VISUAL_DESIGNER ||
            personalisation.getChosenVariant(this.personalisationContext) ===
              'visual_design',
        )}
        @dialog-closed=${this.removePersonalisationData}
        hasAccessibleFocusOnContent
      >
        ${this.chosenPersonalisation ===
          ChosenPersonalisationType.VISUAL_DESIGNER ||
        personalisation.getChosenVariant(this.personalisationContext) ===
          'visual_design'
          ? html`
              <div class="visual-designer-container">
                <fg-visual-designer
                  @visual-designer-save=${this.visualDesignerSave}
                  @visual-designer-close=${this.closeDialog}
                  .designId=${this.designId}
                  .printOption=${this.printOption}
                  .printOptionCode=${this.printOption?.code || ''}
                ></fg-visual-designer>
              </div>
            `
          : html`
              <span slot="title">${this.getStepTitle()}</span>
              <span slot="pre-body">${this.getBackButton()}</span>
              <div class="body">
                <div class="print-option-summary">
                  <slot name="print-option-summary"></slot>
                </div>
                ${this.errorMessage
                  ? html`
                      <fg-personalisation-error
                        part=${this.globalContext.layout}
                        .errorMessage=${this.errorMessage}
                      ></fg-personalisation-error>
                    `
                  : nothing}
                <div>
                  <fg-aria-live
                    .announcement=${this.announcement}
                  ></fg-aria-live>
                  ${this.isVisualDesignerAvailable &&
                  !this.isMobile &&
                  this.chosenPersonalisation !==
                    ChosenPersonalisationType.MANUAL &&
                  !personalisation.getChosenVariant(this.personalisationContext)
                    ? html`
                        <fg-personalisation-choose-visual-designer
                          @handle-announcement=${(event: CustomEvent) => {
                            this.announcement = event.detail.announcement;
                          }}
                          @select-method=${(event: CustomEvent) => {
                            const { visualDesigner, designId } = event.detail;
                            if (visualDesigner) {
                              this.designId = designId;
                              this.chosenPersonalisation =
                                ChosenPersonalisationType.VISUAL_DESIGNER;
                            } else {
                              this.chosenPersonalisation =
                                ChosenPersonalisationType.MANUAL;
                            }
                          }}
                        ></fg-personalisation-choose-visual-designer>
                      `
                    : this.getStepContent()}
                </div>
              </div>
              <p slot="footer">
                ${html`
                  ${this.isVisualDesignerAvailable
                    ? t('content.personalisation.visualDesignerPrintProof')
                    : t('content.personalisation.freePrintProof')}
                `}
              </p>
            `}
      </fg-dialog>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      .body {
        background: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        padding: 8px;
      }

      .visual-designer-container {
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        height: 90vh;
        width: 90vw;
      }

      button {
        color: var(--fg-primary, var(--fg-fallback-primary));
      }
      button:hover,
      button:focus {
        text-decoration: underline;
      }

      .previous-button {
        align-items: center;
        display: flex;
        padding-bottom: 4px;
      }

      .previous-icon {
        height: 20px;
        margin-right: 2px;
        width: 9px;
      }

      .print-option-summary {
        margin-bottom: 15px;
      }
    `,
  ];
}
